import React from 'react';
import type { ClassScheduleSettings } from '../services/sectionLayoutService';
import type {
  AcademicTerm,
  AnnualTimetableCell,
  Grade,
  ProgramProgressCell,
  ProgramScopedContentRow,
  Track,
} from '../types/domain';
import {
  invalidateProgramProgressCellsQuery,
  invalidateProgramScopedRowsQuery,
} from '../query/cacheUpdates';
import { useAnnualProgressMetaQuery } from '../query/hooks/useAnnualProgressMetaQuery';
import { useAnnualProgressTimetableCellsBulkQuery } from '../query/hooks/useAnnualProgressTimetableCellsBulkQuery';
import { useCalendarDaysQuery } from '../query/hooks/useCalendarDaysQuery';
import { useProgramProgressCellsQuery } from '../query/hooks/useProgramProgressCellsQuery';
import { useProgramScopedRowsQuery } from '../query/hooks/useProgramScopedRowsQuery';
import { buildAnnualCellKey } from '../utils/annualTimetable';
import type { OffDayByDate } from '../utils/annualProgressDataCache';
import { loadAnnualProgressDataCache, saveAnnualProgressDataCache } from '../utils/annualProgressDataCache';
import {
  mergedTimetableCellsReadyForGrades,
  resolveMergedTimetableCellsByKey,
} from '../utils/timetableCellsByKeyResolve';

export type UseAnnualProgressPageQueriesParams = {
  workspaceId: string;
  grades: Grade[];
  academicYear: number;
  termNumber: 1 | 2;
  selectedGradeId: string;
  metaGradeId: string;
  isActive: boolean;
  schoolScopedCacheSyncReady: boolean;
  annualTimetableSaveReloadToken: number;
  programRefreshTick: number;
  annualAggregateRefreshNonce: number;
  programCellsFetchNonce: number;
};

export type UseAnnualProgressPageQueriesResult = {
  terms: AcademicTerm[];
  schedule: ClassScheduleSettings | null;
  tracks: Track[];
  regularColors: string[];
  dedicatedColors: string[];
  baseExcludeColors: string[];
  holidaySet: Set<string>;
  offDayByDate: OffDayByDate;
  cellsByKey: Record<string, AnnualTimetableCell>;
  programCellsByKey: Record<string, ProgramProgressCell>;
  scopedRowsForTerm: ProgramScopedContentRow[];
  metaLoading: boolean;
  annualCellsLoading: boolean;
  programDataLoading: boolean;
  metaError: string | null;
};

function buildProgramCellsMap(rows: ProgramProgressCell[]): Record<string, ProgramProgressCell> {
  const map: Record<string, ProgramProgressCell> = {};
  for (const row of rows) {
    map[buildAnnualCellKey(row.grade_id, row.monday_iso, row.weekday_key, row.slot_index)] = row;
  }
  return map;
}

export function useAnnualProgressPageQueries(
  params: UseAnnualProgressPageQueriesParams
): UseAnnualProgressPageQueriesResult {
  const {
    workspaceId,
    academicYear,
    termNumber,
    selectedGradeId,
    metaGradeId,
    isActive,
    schoolScopedCacheSyncReady,
    annualTimetableSaveReloadToken,
    programRefreshTick,
    annualAggregateRefreshNonce,
    programCellsFetchNonce,
  } = params;

  const queryEnabled = isActive && schoolScopedCacheSyncReady && !!workspaceId.trim();

  const metaQuery = useAnnualProgressMetaQuery({
    workspaceId,
    academicYear,
    termNumber,
    gradeId: metaGradeId,
    enabled: queryEnabled && !!metaGradeId,
  });

  const schedule = metaQuery.data?.schedule ?? null;

  const terms: AcademicTerm[] = metaQuery.data?.terms ?? [];
  const termDef = React.useMemo(
    () => terms.find((t) => t.term_number === termNumber) ?? null,
    [terms, termNumber]
  );

  const processedAnnualCellsSaveTokenRef = React.useRef(0);
  const processedProgramRefreshTickRef = React.useRef(0);
  const lastProgramInvalidateRef = React.useRef('');

  const forceTimetableNetwork = annualTimetableSaveReloadToken > processedAnnualCellsSaveTokenRef.current;
  const forceProgramRefresh = programRefreshTick > processedProgramRefreshTickRef.current;

  const apCache = React.useMemo(
    () => (workspaceId ? loadAnnualProgressDataCache(workspaceId, academicYear, termNumber) : null),
    [academicYear, workspaceId, termNumber, annualAggregateRefreshNonce]
  );
  const calendarHydrated =
    !!apCache?.found && !!apCache.state.holidayHydrated && !!apCache.state.offDaysHydrated;

  const timetableGradeIds = React.useMemo(
    () => (selectedGradeId.trim() ? [selectedGradeId.trim()] : []),
    [selectedGradeId]
  );

  const calendarQuery = useCalendarDaysQuery({
    workspaceId,
    from: termDef?.starts_on ?? '',
    to: termDef?.ends_on ?? '',
    enabled: queryEnabled && !!termDef?.starts_on && !!termDef?.ends_on && !calendarHydrated,
  });

  const timetableQuery = useAnnualProgressTimetableCellsBulkQuery({
    workspaceId,
    academicYear,
    termNumber,
    gradeIds: timetableGradeIds,
    enabled: queryEnabled && timetableGradeIds.length > 0,
    forceNetwork: forceTimetableNetwork,
  });

  const programCellsQuery = useProgramProgressCellsQuery({
    workspaceId,
    academicYear,
    termNumber,
    gradeId: selectedGradeId,
    enabled: queryEnabled && !!selectedGradeId,
    forceRefresh: forceProgramRefresh,
  });

  const scopedRowsQuery = useProgramScopedRowsQuery({
    workspaceId,
    academicYear,
    termNumber,
    gradeId: selectedGradeId,
    enabled: queryEnabled && !!selectedGradeId,
    forceRefresh: forceProgramRefresh,
  });

  React.useEffect(() => {
    const sig = `${programRefreshTick}|${programCellsFetchNonce}`;
    if (!queryEnabled || !selectedGradeId || sig === lastProgramInvalidateRef.current) return;
    lastProgramInvalidateRef.current = sig;
    invalidateProgramProgressCellsQuery(workspaceId, academicYear, termNumber, selectedGradeId);
    invalidateProgramScopedRowsQuery(workspaceId, academicYear, termNumber, selectedGradeId);
  }, [
    academicYear,
    programCellsFetchNonce,
    programRefreshTick,
    queryEnabled,
    selectedGradeId,
    termNumber,
    workspaceId,
  ]);

  React.useEffect(() => {
    if (timetableQuery.isSuccess && forceTimetableNetwork) {
      processedAnnualCellsSaveTokenRef.current = annualTimetableSaveReloadToken;
    }
  }, [annualTimetableSaveReloadToken, forceTimetableNetwork, timetableQuery.isSuccess]);

  React.useEffect(() => {
    if (programCellsQuery.isSuccess && scopedRowsQuery.isSuccess && forceProgramRefresh) {
      processedProgramRefreshTickRef.current = programRefreshTick;
    }
  }, [forceProgramRefresh, programCellsQuery.isSuccess, programRefreshTick, scopedRowsQuery.isSuccess]);

  const holidayBundle = React.useMemo(() => {
    if (calendarHydrated && apCache?.found) {
      return {
        holidaySet: new Set(apCache.state.holidayDays),
        offDayByDate: apCache.state.offDayByDate ?? {},
      };
    }
    const days = calendarQuery.data ?? [];
    const offDayByDate: OffDayByDate = {};
    const holidayDays: string[] = [];
    for (const d of days) {
      if (d.day_kind === 'holiday' || d.day_kind === 'closure' || d.day_kind === 'vacation') {
        holidayDays.push(d.day);
        const note = (d.note ?? '').trim();
        offDayByDate[d.day] = note ? { kind: d.day_kind, note } : { kind: d.day_kind };
      }
    }
    return { holidaySet: new Set(holidayDays), offDayByDate };
  }, [apCache, calendarHydrated, calendarQuery.data]);

  const cellsByKey = React.useMemo(
    () =>
      resolveMergedTimetableCellsByKey({
        workspaceId,
        academicYear,
        termNumber,
        gradeIds: timetableGradeIds,
        networkCellsByKey: timetableQuery.data,
      }),
    [
      academicYear,
      annualAggregateRefreshNonce,
      annualTimetableSaveReloadToken,
      programRefreshTick,
      termNumber,
      timetableGradeIds,
      timetableQuery.data,
      workspaceId,
    ]
  );

  const timetableCellsHydrated = React.useMemo(() => {
    if (!timetableGradeIds.length) return false;
    if (timetableQuery.isSuccess && timetableQuery.data && Object.keys(timetableQuery.data).length > 0) {
      return true;
    }
    return mergedTimetableCellsReadyForGrades(workspaceId, academicYear, termNumber, timetableGradeIds);
  }, [
    academicYear,
    termNumber,
    timetableGradeIds,
    timetableQuery.data,
    timetableQuery.isSuccess,
    workspaceId,
    annualAggregateRefreshNonce,
    programRefreshTick,
  ]);

  React.useEffect(() => {
    if (!queryEnabled || !workspaceId || !metaQuery.data) return;
    saveAnnualProgressDataCache(workspaceId, academicYear, termNumber, {
      terms: metaQuery.data.terms,
      schedule: metaQuery.data.schedule,
      tracks: metaQuery.data.tracks,
      regularColors: metaQuery.data.regularColors,
      dedicatedColors: metaQuery.data.dedicatedColors,
      baseExcludeColors: metaQuery.data.baseExcludeColors,
      holidayDays: Array.from(holidayBundle.holidaySet),
      offDayByDate: holidayBundle.offDayByDate,
      offDaysHydrated: calendarHydrated || (calendarQuery.isSuccess && !!termDef?.starts_on),
      cellsByKey: {},
      holidayHydrated: calendarHydrated || calendarQuery.isSuccess,
      cellsHydrated: timetableCellsHydrated,
    });
  }, [
    academicYear,
    calendarHydrated,
    calendarQuery.isSuccess,
    holidayBundle.offDayByDate,
    holidayBundle.holidaySet,
    metaQuery.data,
    queryEnabled,
    termDef?.starts_on,
    termNumber,
    timetableCellsHydrated,
    workspaceId,
  ]);

  const programCellsByKey = React.useMemo(
    () => buildProgramCellsMap(programCellsQuery.data ?? []),
    [programCellsQuery.data]
  );

  return {
    terms,
    schedule,
    tracks: metaQuery.data?.tracks ?? [],
    regularColors: metaQuery.data?.regularColors ?? [],
    dedicatedColors: metaQuery.data?.dedicatedColors ?? [],
    baseExcludeColors: metaQuery.data?.baseExcludeColors ?? [],
    holidaySet: holidayBundle.holidaySet,
    offDayByDate: holidayBundle.offDayByDate,
    cellsByKey,
    programCellsByKey,
    scopedRowsForTerm: scopedRowsQuery.data ?? [],
    metaLoading: queryEnabled && !!metaGradeId && metaQuery.isLoading,
    annualCellsLoading:
      queryEnabled &&
      timetableGradeIds.length > 0 &&
      (timetableQuery.isLoading || timetableQuery.isFetching) &&
      Object.keys(cellsByKey).length === 0,
    programDataLoading:
      queryEnabled &&
      !!selectedGradeId &&
      (programCellsQuery.isLoading || scopedRowsQuery.isLoading),
    metaError:
      (metaQuery.error instanceof Error ? metaQuery.error.message : metaQuery.error ? String(metaQuery.error) : null),
  };
}
